/**
 * @file utils.ts
 * @description 聊天模块工具方法集：消息裁剪（轮次/时间）、不合规过滤、文件类型与图标、插件命中状态、
 * HTML -> Slate 值解析、尾部空文本补齐、文件名格式化等。
 */
import DOMPurify from 'dompurify'
import { Element as SlateElement, Text } from 'slate'
import { ChatMessageType, LimitInfoType } from './types'

/**
 * 生成有效的上屏消息数组：
 * 1) 将 prompt 作为 system 首条
 * 2) 过滤 ignore 消息
 * 3) 轮次限制（最后 N 轮）+ 时间限制（最后 M 分钟）
 * 4) 过滤不合规会话（status=30 时去掉 user/assistant 对）
 * @param msg 全量消息
 * @param prompt system 提示词
 * @param limitInfo 轮次/时间限制
 * @returns 子集消息（含 system）
 */
export const getValidChatMsg = (
  msg: ChatMessageType[],
  prompt: string,
  limitInfo: LimitInfoType
): ChatMessageType[] => {
  // 把用户设定作为对话第一条
  const systemMessage: ChatMessageType = {
    role: 'system',
    content: [
      {
        type: 'text',
        text: prompt
      }
    ]
  }

  // 只保留用户对话和模型回答
  const chatMsg = msg.filter(item => !item.ignore)

  // 1、根据短期记忆中最后轮次进行控制，
  // 2、时间控制范围内数量>=5,前端只取最后5轮，数量<=5时按时间控制
  const limitCount = limitInfo.remember_mode === 0 ? limitInfo.remember_last_num : 5

  const countLimitedMsg: ChatMessageType[] = []
  let rounds = 0
  let nextType = 'user'

  for (let i = chatMsg.length - 1; i >= 0 && rounds <= limitCount * 2; i--) {
    if (chatMsg[i].role === nextType) {
      countLimitedMsg.unshift(chatMsg[i])
      nextType = nextType === 'user' ? 'assistant' : 'user'
      rounds++
    }
  }

  if (countLimitedMsg.length > 0 && countLimitedMsg[0].role === 'assistant') {
    countLimitedMsg.shift()
  }

  // 按最后轮次控制后再按前端时间控制，暂时解决方案，后期需要后端增加消息的createTime字段
  const nowTime = Date.now()
  const limitMinute = limitInfo.remember_mode === 1 ? limitInfo.remember_last_minute : 60
  const timeLimitedMsg = countLimitedMsg.filter(item => {
    return nowTime - item.createTime <= limitMinute * 60 * 1000
  })

  if (timeLimitedMsg.length > 0 && timeLimitedMsg[0].role === 'assistant') {
    timeLimitedMsg.shift()
  }

  // 增加不合规处理，当出现一条不合规消息时过滤user和对应assistant
  const legalMgs = filterIllegalMessages(timeLimitedMsg)

  const subMsg: ChatMessageType[] = [systemMessage]
  if (chatMsg[0]?.role !== 'system') {
    subMsg.push(...legalMgs)
  } else {
    subMsg.push(...legalMgs.slice(1))
  }
  return subMsg
}

/**
 * 检查当前消息是否有 status:30，若有则过滤对应的 assistant 与紧邻的 user
 * @param messages 消息列表
 * @returns 过滤后的消息
 */
function filterIllegalMessages(messages) {
  const revertMessages = messages.reverse()
  const filteredMessages = []
  let skipNext = false

  for (let i = 0; i < revertMessages.length; i++) {
    const message = revertMessages[i]

    if (message.status === 30) {
      skipNext = true
      continue
    }

    if (skipNext) {
      skipNext = false
      continue
    }

    if (message.role === 'assistant' && message?.content?.[0]?.type === 'image_url') {
      filteredMessages.push({
        ...message,
        content: [
          {
            text: '文生图',
            type: 'text'
          }
        ]
      })
    } else {
      filteredMessages.push(message)
    }
  }

  return filteredMessages.reverse()
}

/** 获取文件扩展名（小写） */
export const getFileType = fileName => {
  if (!fileName) return ''
  return fileName.split('.').pop()?.toLowerCase()
}
/** 根据扩展名返回静态图标路径 */
export const getFileIconByType = type => {
  if (!type) return ''
  if (['doc', 'docx'].includes(type)) {
    return '/file_types/word.png'
  } else if (['pdf'].includes(type)) {
    return '/file_types/pdf.png'
  } else if (['ppt', 'pptx'].includes(type)) {
    return '/file_types/ppt.png'
  } else if (['wav', 'pcm', 'ogg', 'opus', 'speex', 'silk', 'mp3', 'm4a', 'aac', 'amr'].includes(type)) {
    return '/file_types/mp3.png'
  } else if (['txt'].includes(type)) {
    return '/file_types/txt.png'
  } else if (['excel', 'xlsx', 'xls', 'csv'].includes(type)) {
    return '/file_types/excel.png'
  } else if (['md'].includes(type)) {
    return '/file_types/md.png'
  } else if (['json'].includes(type)) {
    return '/file_types/json.png'
  } else {
    return '/file_types/txt.png'
  }
}

/** 根据文件名返回静态图标路径 */
export const getFileIconByFileName = fileName => {
  if (!fileName) return ''
  const type = getFileType(fileName)
  return getFileIconByType(type)
}

/**
 * 计算会话中定制化插件是否存在
 * @param toolIds 工具ID列表
 * @param toolEnable 总开关
 * @returns { includedAudioPlugin, includedLenovoCloudPlugin }
 */
export const getSpecificPluginResultMap = (toolIds, toolEnable) => {
  const plugins = toolIds || []
  const result = {
    includedAudioPlugin: toolEnable && plugins.includes(10256), // 10256 是语音文件插件的固定id
    includedLenovoCloudPlugin: toolEnable && plugins.includes(10884) // 10884 是云文档上传插件的固定id
  }
  return result
}

/* 解析slate节点函数 */
/**
 * 将 HTML 字符串解析为 Slate 值（段落包裹）
 * @param html 含 mention span 的 HTML
 * @returns Slate Value（Descendant[]）
 */
export function parseHtmlToSlateValue(html?: string): any[] {
  if (!html || html.trim() === '') {
    return [
      {
        type: 'paragraph',
        children: [{ text: '' }]
      }
    ]
  }

  // 使用 DOMPurify 清理 HTML，防止 XSS 攻击
  // 只允许 mention span 标签，移除所有其他危险标签和属性
  const sanitized = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['span'], // 只允许 span 标签（用于 mention）
    ALLOWED_ATTR: ['class', 'data-id'], // 只允许 class 和 data-id 属性
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'link', 'meta', 'style', 'img'], // 明确禁止危险标签
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'onchange', 'onsubmit'], // 禁止事件属性
    KEEP_CONTENT: true // 保留文本内容
  })

  const div = document.createElement('div')
  div.innerHTML = sanitized

  const children = []

  div.childNodes.forEach(node => {
    children.push(...parseNode(node))
  })

  return [
    {
      type: 'paragraph',
      children: children.length > 0 ? children : [{ text: '' }]
    }
  ]
}

/**
 * 递归解析 DOM 节点到 Slate children
 * @param node ChildNode
 */
function parseNode(node: ChildNode): any[] {
  if (node.nodeType === Node.TEXT_NODE) {
    return [{ text: node.textContent || '' }]
  }

  if (node.nodeType === Node.ELEMENT_NODE) {
    const el = node as HTMLElement

    if (el.classList.contains('mention')) {
      const id = el.dataset.id || ''
      const text = el.textContent || '' // 获取显示文本，去掉 @ 符号
      const name = text.startsWith('@') ? text.slice(1) : text // 去掉 @ 符号
      return [
        {
          type: 'mention',
          name: name, // 使用实际显示的名称
          id: id, // 保存 id
          key: id, // 同时保存为 key
          inline: true,
          children: [{ text: '' }]
        }
      ]
    }

    // 递归处理嵌套
    const children = []
    node.childNodes.forEach(child => {
      children.push(...parseNode(child))
    })
    return children
  }

  return []
}
/* 添加空文本 */
/**
 * 若最后一个子节点非 Text，补一个空文本，保证光标可见
 */
export function ensureTextAtEnd(value: any) {
  const last = value[value.length - 1] as any
  if (
    SlateElement.isElement(last) &&
    last.children.length > 0 &&
    !Text.isText(last.children[last.children.length - 1])
  ) {
    // 如果最后 child 不是 Text，就加一个空文本
    last.children.push({ text: '' })
  }
  return value
}

/**
 * 文件名格式化，保留扩展名，主体过长则中间省略
 * @param name 文件名
 * @param maxLen 最大显示长度，默认15
 */
export function formatFileName(name: string, maxLen = 15) {
  if (!name) return ''
  if (name.length <= maxLen) return name
  const extIndex = name.lastIndexOf('.')
  const ext = extIndex !== -1 ? name.slice(extIndex) : ''
  const prefixLen = Math.max(6, maxLen - ext.length)
  return `${name.slice(0, prefixLen)}...${ext}`
}

/**
 * 判断 OSS 链接是否过期
 * @param url OSS 链接（包含 Expires 参数）
 * @returns 是否过期
 */
export const isUrlExpired = (url: string): boolean => {
  if (!url) return true

  try {
    // 解析 URL 中的 Expires 参数
    const urlObj = new URL(url)
    const expiresParam = urlObj.searchParams.get('Expires')

    if (!expiresParam) {
      // 如果没有 Expires 参数，认为未过期（可能是永久链接）
      return false
    }

    // Expires 是 Unix 时间戳（秒）
    const expiresTimestamp = parseInt(expiresParam, 10)
    const currentTimestamp = Math.floor(Date.now() / 1000)

    // 如果当前时间大于过期时间，则已过期
    return currentTimestamp >= expiresTimestamp
  } catch (error) {
    console.error('判断链接是否过期失败:', error)
    // 解析失败，默认认为未过期
    return false
  }
}
